import {
  BillingStatus,
  ShiftNeed,
  TimeEntryBreakDto
} from '@umss/shared/data-access';
import dayjs from 'dayjs';

/**
 * UI model for displaying shift cards in the Find Shifts feature
 * Extends ShiftNeed with additional display properties for contractor UI
 */
export interface ShiftCard extends ShiftNeed {
  // Additional UI-specific fields
  title: string; // Derived from scopeOfPracticeDisplay or position
  date: string; // Formatted date from shiftDate
  duration: number; // Calculated duration in hours
  distance: string; // Calculated distance from contractor location
  scopesOfPractice: string[]; // Array of scope labels for badges
  totalPay: number; // Same as estimatedPayment
  overtimeRate: number | null; // Calculated overtime rate
  facilityAddress?: string;
  facilityTimezone?: string;
  specialties?: string[];
  paymentSchedule?: string;
  isRecurring?: boolean;
  assignedNurse?: {
    name: string;
    avatar?: string;
  };
  applicationStatus?: string;
  breaks?: TimeEntryBreakDto[];
  isEditable?: boolean;
  timesheetStatus?: BillingStatus;
  shiftNeedIsFull?: boolean;
  clockInWindowStart?: string;
  clockInWindowEnd?: string;
  clockOutWindowStart?: string;
  clockOutWindowEnd?: string;
  clockInNote?: string;
  clockOutNote?: string;
  requiresClockInNote?: boolean;
  requiresClockOutNote?: boolean;
}

/**
 * Helper function to map ShiftNeed to ShiftCard for UI display
 */
export function mapShiftNeedToCard(shiftNeed: ShiftNeed): ShiftCard {
  const contractorRate =
    shiftNeed.rateInfo?.contractorNormalHourlyRate ?? shiftNeed.hourlyRate ?? 0;
  const overtimeRate =
    shiftNeed.latestCounterOfferRate ??
    (shiftNeed.isUrgent
      ? shiftNeed.rateInfo?.contractorUrgentHourlyRate
      : null) ??
    shiftNeed.rateInfo?.contractorNormalHourlyRate ??
    shiftNeed.hourlyRate ??
    0;
  const contractorPayment =
    shiftNeed.rateInfo?.contractorNormalEstimatedPayment ?? 0;

  return {
    ...shiftNeed, // Spread all ShiftNeed properties
    // Use contractor rate (excludes differential/margin) instead of facility rate
    hourlyRate: contractorRate,
    estimatedPayment: contractorPayment,
    // Add UI-specific properties
    title: shiftNeed.facilityName || 'Facility',
    date: dayjs(shiftNeed.shiftDate).format('dddd, MMMM D, YYYY'),
    duration: shiftNeed.estimatedHours,
    distance: shiftNeed.distanceMiles
      ? `${shiftNeed.distanceMiles} mi`
      : '0 mi',
    scopesOfPractice: [shiftNeed.scopeOfPracticeDisplay],
    totalPay: contractorPayment,
    overtimeRate: overtimeRate, // Standard overtime calculation
    facilityAddress: shiftNeed.facilityFullAddress
  };
}
